// Usage:
//
//   <ProSubscribeModal
//     paddleConfigUrl="/api/v1/users/paddle_config"
//     onSubscribed={({ isPro }) => this.setState({ isPro })}
//   />
//
//   // Custom button text/class
//   <ProSubscribeModal
//     paddleConfigUrl="/api/v1/users/paddle_config"
//     buttonText="Upgrade"
//     buttonClass="btn btn-sm btn-success"
//   />
//
//   // Wrap your own trigger element
//   <ProSubscribeModal paddleConfigUrl="/api/v1/users/paddle_config">
//     <span className="upgrade-link">Upgrade to Pro</span>
//   </ProSubscribeModal>
//
// Props:
//   paddleConfigUrl (required) - endpoint returning Paddle config JSON
//   onSubscribed({ isPro }) - called after successful subscription
//   onClose() - called when success screen is dismissed
//   buttonText - custom button label (default: "Get Pro")
//   buttonClass - custom button CSS class
//   showBenefits - show Pro benefits list above plan cards (default: false)
//   children - custom trigger element (replaces default button)
//
// Paddle Classic JS is loaded automatically if not already on the page.

import React from "react";

import { trackAmplitudeEvent } from "../amplitude";
import { discountedSalePrice, isFullPriceProduct, perMonthAmount } from "../paddlePrice";
import Modal from "./Modal";
import Panel from "./Panel";

export default class ProSubscribeModal extends React.Component {
  constructor(props) {
    super(props);

    this.checkUserStatusCount = 0;

    this.state = {
      config: null,    // fetched from /api/v1/users/paddle_config
      prices: {},      // { [productId]: { listPrice, currency, trialDays, interval } }
      discounts: {},   // { [productId]: discountedPrice }
      showModal: false,
      checkingOut: false,
      success: false
    };
  }

  componentDidMount() {
    this.fetchConfig();
  }

  fetchConfig() {
    const { paddleConfigUrl } = this.props;

    $.ajax({ url: paddleConfigUrl })
      .done((config) => {
        this.setState({ config }, () => this.setupPaddle());
      });
  }

  loadPaddleScript() {
    return new Promise((resolve) => {
      if (typeof Paddle !== "undefined") return resolve();
      if (document.querySelector('script[src*="paddle.com/paddle/paddle.js"]')) {
        // Script tag exists but hasn't loaded yet
        const existing = document.querySelector('script[src*="paddle.com/paddle/paddle.js"]');
        existing.addEventListener("load", resolve);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://cdn.paddle.com/paddle/paddle.js";
      script.onload = resolve;
      document.head.appendChild(script);
    });
  }

  setupPaddle() {
    const { config } = this.state;
    if (!config) return;

    if (config.sandbox) {
      // expose for test/sandbox checkout simulation
      window.handleProSubscribeModalCheckoutCompleted = (data) => this.handleCheckoutCompleted(data);
    }

    this.loadPaddleScript().then(() => this.initPaddle());
  }

  initPaddle() {
    const { config } = this.state;
    if (!config || typeof Paddle === "undefined") return;

    if (config.sandbox) {
      Paddle.Environment.set("sandbox");
    }

    Paddle.Setup({
      eventCallback: (data) => {
        if (data.event === "Checkout.Close") {
          if (data.eventData.checkout.completed) {
            this.handleCheckoutCompleted(data);
          } else {
            this.setState({ checkingOut: false });
          }
        }
      },
      vendor: parseInt(config.vendor)
    });

    this.fetchPrices();
  }

  fetchPrices() {
    const { config } = this.state;
    const { couponCode, sandbox, monthlyProductId, yearlyProductId, lifetimeProductId } = config;

    const productIds = [monthlyProductId, yearlyProductId, lifetimeProductId].filter(Boolean).join(",");

    $.ajax({
      dataType: "jsonp",
      data: {
        calculate_recurring_discounts: true,
        product_ids: productIds,
        coupons: couponCode
      },
      timeout: 3000,
      url: `https://${sandbox ? "sandbox-" : ""}checkout.paddle.com/api/2.0/prices`
    })
      .done((data) => {
        if (!data.success) {
          this.fallbackFetchPrices();
          return;
        }

        try {
          const prices = {};
          const discounts = {};

          data.response.products.forEach((r) => {
            const listPrice = r.subscription ? r.subscription.list_price.gross : r.list_price.gross;
            const currencyFormat = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: r.currency
            });
            const prettify = (price) => currencyFormat.format(price);
            const prettifyPerMonth = (yearlyPrice) => (
              prettify(perMonthAmount(yearlyPrice, currencyFormat.resolvedOptions().maximumFractionDigits))
            );

            prices[r.product_id] = {
              listPrice: prettify(listPrice),
              listPriceRaw: listPrice,
              currency: r.currency,
              trialDays: r.subscription && r.subscription.trial_days,
              interval: r.subscription && r.subscription.interval
            };

            // Only subscriptions go on sale; lifetime (a one-off product) is always full price.
            const isOnSale = r.subscription && !isFullPriceProduct(r.product_id, config.fullPriceProductIds);
            const discountedPrice = isOnSale ? discountedSalePrice(r, config) : null;
            if (discountedPrice !== null) {
              discounts[r.product_id] = {
                price: prettify(discountedPrice),
                priceRaw: discountedPrice,
                perMonth: r.subscription && r.subscription.interval === "year" ? prettifyPerMonth(discountedPrice) : null
              };
            }

            if (r.subscription && r.subscription.interval === "year") {
              prices[r.product_id].perMonth = prettifyPerMonth(listPrice);
            }
          });

          this.setState({ prices, discounts });
        } catch (e) {
          this.fallbackFetchPrices();
        }
      })
      .fail(() => {
        this.fallbackFetchPrices();
      });
  }

  fallbackFetchPrices() {
    const { config } = this.state;
    const { monthlyProductId, yearlyProductId, lifetimeProductId } = config;
    const productIds = [monthlyProductId, yearlyProductId, lifetimeProductId].filter(Boolean);

    productIds.forEach((productId) => {
      Paddle.Product.Prices(productId, (data) => {
        const grossPrice = data.recurring ? data.recurring.price.gross : data.price.gross;
        const interval = data.recurring && data.recurring.subscription && data.recurring.subscription.type;
        const trialDays = data.recurring && data.recurring.subscription && data.recurring.subscription.trial_days;

        this.setState((prev) => ({
          prices: {
            ...prev.prices,
            [productId]: {
              listPrice: grossPrice,
              interval,
              trialDays
            }
          }
        }));
      });
    });
  }

  isFullPrice(productId) {
    const { config } = this.state;

    return productId === config.lifetimeProductId || isFullPriceProduct(productId, config.fullPriceProductIds);
  }

  openCheckout(productId) {
    const { config } = this.state;

    this.setState({ checkingOut: true });

    Paddle.Checkout.open({
      product: productId,
      email: config.userEmail,
      marketingConsent: config.marketingConsent ? 1 : 0,
      passthrough: (config.productPassthroughs || {})[productId] || config.userPassthrough,
      coupon: this.isFullPrice(productId) ? undefined : (config.couponCode || undefined),
      allowQuantity: false
    });
  }

  handleCheckoutCompleted(data) {
    this.setState({ checkingOut: true }, () => this.checkUserStatus(data));
  }

  checkUserStatus(eventCallbackData) {
    if (this.checkUserStatusCount >= 5) {
      return this.onSubscribed(eventCallbackData);
    }

    const { config } = this.state;

    $.ajax({ url: config.isProSubscriberUrl })
      .done((data) => {
        if (data.user.isPro) {
          return this.onSubscribed(eventCallbackData);
        }
        this.checkUserStatusCount += 1;
        setTimeout(() => this.checkUserStatus(eventCallbackData), 2000);
      })
      .fail(() => {
        this.onSubscribed(eventCallbackData);
      });
  }

  onSubscribed(eventCallbackData) {
    this.setState({ checkingOut: false, success: true });

    const { onSubscribed } = this.props;
    if (onSubscribed) {
      onSubscribed({ isPro: true });
    }

    try {
      trackAmplitudeEvent("web_app_converted_to_pro");
    } catch (e) {
      // tracking is non-critical
    }
  }

  renderPlanCard({ productId, label, period, highlight, showGuarantee, isLifetime }) {
    const { prices, discounts } = this.state;
    const price = prices[productId];
    const discount = discounts[productId];

    return (
      <div className="panel panel-default text-center subscription-plan" style={{ position: "relative" }}>
        <div className="panel-body">
          {/*<div style={{ fontSize: highlight ? "2.75em" : "2em", fontWeight: highlight ? "bold" : "normal", marginTop: highlight ? 10 : 0 }}>*/}
          <div style={{ fontSize: "2em", fontWeight: "normal", marginTop: highlight ? 10 : 0 }}>
            {label}
          </div>
          <div className="price">
            {discount ? (
              <>
                <div style={{ fontSize: "2em", textDecoration: "line-through" }}>
                  {price ? price.listPrice : "Loading..."}
                </div>
                <div style={{ color: "#5cb85c", fontSize: "3em", fontWeight: "bold" }}>
                {/*<div style={{ color: "#5cb85c", fontSize: highlight ? "4em" : "3em", fontWeight: "bold" }}>*/}
                  {discount.price}
                </div>
              </>
            ) : (
              <div style={{ fontSize: "3em", fontWeight: "bold" }}>
              {/*<div style={{ fontSize: highlight ? "4em" : "3em", fontWeight: "bold" }}>*/}
                {price ? price.listPrice : "Loading..."}
              </div>
            )}
          </div>
          <div>{period}</div>
          {price && price.trialDays ? (
            <div><strong>{price.trialDays} day free trial</strong></div>
          ) : null}
          {price && price.perMonth && !discount ? (
            <div style={{ color: "#aaa" }}>{price.perMonth}/month</div>
          ) : null}
          {discount && discount.perMonth ? (
            <div style={{ color: "#aaa" }}>{discount.perMonth}/month</div>
          ) : null}
          {showGuarantee && <div>30-day money back guarantee</div>}
          <button
            className={`btn btn-lg btn-block ${highlight ? "btn-success" : "btn-success-outline"}`}
            style={{ fontFamily: "joystix", marginTop: 15 }}
            onClick={() => this.openCheckout(productId)}
          >
            {isLifetime ? "Buy Now" : "Subscribe"}
          </button>
          {highlight && (
            <div className="joystix" style={{ textTransform: "uppercase", position: "absolute", top: 0, right: 0, padding: "4px 10px", background: "#5cb85c", borderBottomLeftRadius: 10, fontSize: 12, fontWeight: "bold", color: "#000" }}>
              Best Deal
            </div>
          )}
        </div>
      </div>
    );
  }

  renderSuccess() {
    return (
      <div className="text-center" style={{ padding: "40px 20px" }}>
        <h2 className="joystix" style={{ color: "#5cb85c" }}>Welcome to Pro!</h2>
        <p style={{ fontSize: "1.2em", margin: "20px 0" }}>
          Your subscription is active. Enjoy unlimited access!
        </p>
        <button
          className="btn btn-success btn-lg joystix"
          onClick={() => {
            this.setState({ showModal: false });
            if (this.props.onClose) this.props.onClose();
          }}
        >
          Let's Go!
        </button>
      </div>
    );
  }

  renderBenefits() {
    if (!this.props.showBenefits) return null;

    const benefits = [
      { icon: "glyphicon-play", text: "Unlimited sentences per day" },
      { icon: "glyphicon-cog", text: "Custom rounds, review intervals & more" },
      // { icon: "glyphicon-volume-up", text: "Listening & speaking practice" },
      { icon: "glyphicon-ban-circle", text: "No ads" },
      { icon: "glyphicon-stats", text: "Detailed stats & review forecast" },
      { icon: "glyphicon-star", text: "Favorites, sentence search & grammar" },
      { icon: "glyphicon-download-alt", text: "Offline play in the mobile app" },
    ];

    const half = Math.ceil(benefits.length / 2);
    const left = benefits.slice(0, half);
    const right = benefits.slice(half);

    const renderList = (items) => items.map((b, i, arr) => (
      <div key={i} style={{ alignItems: "center", display: "flex", marginBottom: i === arr.length - 1 ? 0 : 24 }}>
        <span className={`glyphicon ${b.icon}`} style={{ color: "#5cb85c", marginRight: 10, fontSize: "1.1em", width: 20, display: "inline-block", textAlign: "center" }} />
        <span style={{ fontSize: "1.05em" }}>{b.text}</span>
      </div>
    ));

    return (
      <div style={{ marginBottom: 20 }}>
        <h4 className="joystix" style={{ marginTop: 0, marginBottom: 16, fontSize: "1.1em" }}>Get fluent even faster</h4>
        <Panel>
          <div className="row">
            <div className="col-xs-12 col-sm-6">{renderList(left)}</div>
            <div className="col-xs-12 col-sm-6">{renderList(right)}</div>
          </div>
        </Panel>
      </div>
    );
  }

  renderPlans() {
    const { config, checkingOut } = this.state;
    if (!config) return null;
    const isBlocked = checkingOut || config.purchaseProcessing;

    const { monthlyProductId, yearlyProductId, lifetimeProductId } = config;
    // Three plans only fit side by side in the large modal, which starts at the md breakpoint.
    const colClass = lifetimeProductId ? "col-xs-12 col-md-4" : "col-xs-12 col-sm-6";

    return (
      <div>
        {isBlocked && (
          <div className="text-center" style={{ padding: 20 }}>
            <p>{checkingOut ? "Processing your subscription..." : "Your purchase is processing. It can take a few minutes."}</p>
          </div>
        )}
        <div className="row" style={{ opacity: isBlocked ? 0.5 : 1, pointerEvents: isBlocked ? "none" : "auto" }}>
          <div className={colClass}>
            {this.renderPlanCard({
              productId: monthlyProductId,
              label: "Monthly",
              period: "per month*"
            })}
          </div>
          <div className={colClass}>
            {this.renderPlanCard({
              productId: yearlyProductId,
              label: "Yearly",
              period: "per year*",
              highlight: true,
              showGuarantee: true
            })}
          </div>
          {lifetimeProductId && (
            <div className={colClass}>
              {this.renderPlanCard({
                productId: lifetimeProductId,
                label: "Lifetime",
                period: "One-time purchase",
                showGuarantee: true,
                isLifetime: true
              })}
            </div>
          )}
        </div>
        <p className="text-center" style={{ marginTop: 10, color: "#aaa" }}>
          *Recurring billing. Cancel anytime.
        </p>
      </div>
    );
  }

  modalSize() {
    const { config } = this.state;

    return config && config.lifetimeProductId ? "large" : undefined;
  }

  render() {
    const { buttonClass, buttonText, children } = this.props;
    const { showModal, success } = this.state;

    return (
      <>
        {children ? (
          <span onClick={() => this.setState({ showModal: true })}>{children}</span>
        ) : (
          <button
            className={buttonClass || "btn btn-success btn-lg joystix"}
            onClick={() => this.setState({ showModal: true })}
          >
            {buttonText || "Get Pro"}
          </button>
        )}
        {showModal && (
          <Modal
            title={success ? "" : "Clozemaster Pro"}
            size={this.modalSize()}
            onHidden={() => this.setState({ showModal: false })}
          >
            {success ? this.renderSuccess() : <>{this.renderBenefits()}{this.renderPlans()}</>}
          </Modal>
        )}
      </>
    );
  }
}
